export type ServiceRequestTransitionAction = "ACKNOWLEDGE" | "COMPLETE";

export interface ServiceRequestTransitionCommand {
  eventId: string;
  action: ServiceRequestTransitionAction;
  expectedAssignedActorDisplayName: string | null;
  expectedAssignedToCurrentActor: boolean;
  idempotencyKey: string;
}

export type ServiceRequestTransitionRecoveryState = "CLAIMED" | "OUTCOME_UNKNOWN";

export interface ServiceRequestTransitionRecovery {
  ownerIdentityKey: string;
  command: ServiceRequestTransitionCommand;
  state: ServiceRequestTransitionRecoveryState;
}

export type ServiceRequestTransitionRecoveryView =
  | { status: "NONE" }
  | { status: "OWNED"; recovery: ServiceRequestTransitionRecovery }
  | { status: "OTHER_OPERATOR" }
  | { status: "RECOVERY_STORAGE_UNAVAILABLE" };

interface StorageAccess {
  browser: boolean;
  storage: Storage | null;
}

const definitelyNoEffectAfterIdempotencyLookup = new Set([
  "IDEMPOTENCY_COLLISION",
  "SERVICE_REQUEST_ALREADY_ASSIGNED",
  "SERVICE_REQUEST_ASSIGNEE_MISMATCH",
  "SERVICE_REQUEST_FINALIZED",
  "SERVICE_REQUEST_NOT_ACKNOWLEDGED",
  "SERVICE_REQUEST_NOT_FOUND",
]);

const definitelyNoEffectForClaimedAttempt = new Set([
  ...definitelyNoEffectAfterIdempotencyLookup,
  "AUTH_REQUIRED",
  "AUTH_CREDENTIAL_CONFLICT",
  "AUTHORIZATION_DEFAULT_DENY",
  "CSRF_REJECTED",
  "FORBIDDEN",
  "MOBILE_SESSION_ACTIVATION_REQUIRED",
  "NATIVE_APP_VERSION_INVALID",
  "NATIVE_APP_VERSION_MISMATCH",
  "NATIVE_APP_VERSION_REQUIRED",
  "NATIVE_SECURE_SESSION_UNAVAILABLE",
  "NATIVE_SERVER_UPDATE_REQUIRED",
  "NATIVE_UPDATE_REQUIRED",
  "ORIGIN_REJECTED",
  "REQUEST_VALIDATION_FAILED",
]);

export const SERVICE_REQUEST_TRANSITION_RECOVERY_STORAGE_KEY =
  "billiard-erp:service-request-transition-recovery:v1";

let pendingRecovery: ServiceRequestTransitionRecovery | null = null;
let dispatchingRecovery: ServiceRequestTransitionRecovery | null = null;
let hydrated = false;
let recoveryUnavailable = false;

export function serviceRequestTransitionIdentityKey(operator: {
  tenantId: string;
  storeId: string;
  id: string;
}): string {
  return JSON.stringify([operator.tenantId, operator.storeId, operator.id]);
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === "object" && !Array.isArray(value);
}

export function serviceRequestTransitionOutcomeIsUnknown(
  error: unknown,
  state: ServiceRequestTransitionRecoveryState,
): boolean {
  if (!isRecord(error) || typeof error.code !== "string") return true;
  return !(state === "CLAIMED"
    ? definitelyNoEffectForClaimedAttempt.has(error.code)
    : definitelyNoEffectAfterIdempotencyLookup.has(error.code));
}

function hasExactKeys(record: Record<string, unknown>, keys: readonly string[]): boolean {
  return (
    Object.keys(record).length === keys.length && keys.every((key) => Object.hasOwn(record, key))
  );
}

function isBoundedTrimmedString(value: unknown, minimum: number, maximum: number): value is string {
  return (
    typeof value === "string" &&
    value === value.trim() &&
    [...value].length >= minimum &&
    [...value].length <= maximum
  );
}

function isIdentityKey(value: unknown): value is string {
  if (!isBoundedTrimmedString(value, 1, 1_024)) return false;
  try {
    const identity = JSON.parse(value) as unknown;
    return (
      Array.isArray(identity) &&
      identity.length === 3 &&
      identity.every((part) => isBoundedTrimmedString(part, 1, 256))
    );
  } catch {
    return false;
  }
}

function isServiceRequestTransitionCommand(
  value: unknown,
): value is ServiceRequestTransitionCommand {
  if (
    !isRecord(value) ||
    !hasExactKeys(value, [
      "eventId",
      "action",
      "expectedAssignedActorDisplayName",
      "expectedAssignedToCurrentActor",
      "idempotencyKey",
    ])
  ) {
    return false;
  }
  return (
    isBoundedTrimmedString(value.eventId, 1, 256) &&
    (value.action === "ACKNOWLEDGE" || value.action === "COMPLETE") &&
    (value.expectedAssignedActorDisplayName === null ||
      isBoundedTrimmedString(value.expectedAssignedActorDisplayName, 1, 256)) &&
    typeof value.expectedAssignedToCurrentActor === "boolean" &&
    isBoundedTrimmedString(value.idempotencyKey, 8, 128)
  );
}

function immutableRecovery(
  ownerIdentityKey: string,
  command: ServiceRequestTransitionCommand,
  state: ServiceRequestTransitionRecoveryState,
): ServiceRequestTransitionRecovery {
  return Object.freeze({
    ownerIdentityKey,
    command: Object.freeze(structuredClone(command)),
    state,
  });
}

function storedRecovery(value: unknown): ServiceRequestTransitionRecovery | null {
  if (
    !isRecord(value) ||
    !hasExactKeys(value, ["version", "recovery"]) ||
    value.version !== 1 ||
    !isRecord(value.recovery)
  ) {
    return null;
  }
  const recovery = value.recovery;
  if (
    !hasExactKeys(recovery, ["ownerIdentityKey", "command", "state"]) ||
    !isIdentityKey(recovery.ownerIdentityKey) ||
    !isServiceRequestTransitionCommand(recovery.command) ||
    (recovery.state !== "CLAIMED" && recovery.state !== "OUTCOME_UNKNOWN")
  ) {
    return null;
  }
  return immutableRecovery(recovery.ownerIdentityKey, recovery.command, recovery.state);
}

function recoveryEquals(
  left: ServiceRequestTransitionRecovery,
  right: ServiceRequestTransitionRecovery,
): boolean {
  return (
    left.ownerIdentityKey === right.ownerIdentityKey &&
    left.state === right.state &&
    JSON.stringify(left.command) === JSON.stringify(right.command)
  );
}

function sessionStorageAccess(): StorageAccess {
  if (typeof window === "undefined") return { browser: false, storage: null };
  try {
    return {
      browser: true,
      storage: typeof sessionStorage === "undefined" ? null : sessionStorage,
    };
  } catch {
    return { browser: true, storage: null };
  }
}

function readStoredRecovery(storage: Storage): ServiceRequestTransitionRecovery | null {
  const encoded = storage.getItem(SERVICE_REQUEST_TRANSITION_RECOVERY_STORAGE_KEY);
  return encoded === null ? null : storedRecovery(JSON.parse(encoded) as unknown);
}

function saveStoredRecovery(recovery: ServiceRequestTransitionRecovery): boolean {
  const access = sessionStorageAccess();
  if (!access.browser) return true;
  if (!access.storage) return false;
  try {
    access.storage.setItem(
      SERVICE_REQUEST_TRANSITION_RECOVERY_STORAGE_KEY,
      JSON.stringify({ version: 1, recovery }),
    );
    const stored = readStoredRecovery(access.storage);
    return stored !== null && recoveryEquals(stored, recovery);
  } catch {
    return false;
  }
}

function verifyStoredRecovery(recovery: ServiceRequestTransitionRecovery): boolean {
  const access = sessionStorageAccess();
  if (!access.browser) return true;
  if (!access.storage) return false;
  try {
    const stored = readStoredRecovery(access.storage);
    return stored !== null && recoveryEquals(stored, recovery);
  } catch {
    return false;
  }
}

function hydrateStoredRecovery(): void {
  if (hydrated) return;
  hydrated = true;
  const access = sessionStorageAccess();
  if (!access.browser) return;
  if (!access.storage) {
    recoveryUnavailable = true;
    return;
  }
  try {
    const encoded = access.storage.getItem(SERVICE_REQUEST_TRANSITION_RECOVERY_STORAGE_KEY);
    if (encoded === null) return;
    const stored = readStoredRecovery(access.storage);
    if (!stored) {
      recoveryUnavailable = true;
      return;
    }
    const recovery =
      stored.state === "CLAIMED"
        ? immutableRecovery(stored.ownerIdentityKey, stored.command, "OUTCOME_UNKNOWN")
        : stored;
    if (stored.state === "CLAIMED" && !saveStoredRecovery(recovery)) {
      recoveryUnavailable = true;
      return;
    }
    pendingRecovery = recovery;
  } catch {
    recoveryUnavailable = true;
  }
}

export function readServiceRequestTransitionRecovery(
  identityKey: string,
): ServiceRequestTransitionRecoveryView {
  hydrateStoredRecovery();
  if (recoveryUnavailable) return { status: "RECOVERY_STORAGE_UNAVAILABLE" };
  if (!pendingRecovery) return { status: "NONE" };
  if (pendingRecovery.ownerIdentityKey !== identityKey) return { status: "OTHER_OPERATOR" };
  return { status: "OWNED", recovery: pendingRecovery };
}

export function claimServiceRequestTransitionRecovery(
  identityKey: string,
  command: ServiceRequestTransitionCommand,
): ServiceRequestTransitionRecoveryView {
  const current = readServiceRequestTransitionRecovery(identityKey);
  if (current.status !== "NONE") return current;
  if (!isIdentityKey(identityKey) || !isServiceRequestTransitionCommand(command)) {
    recoveryUnavailable = true;
    return { status: "RECOVERY_STORAGE_UNAVAILABLE" };
  }
  const recovery = immutableRecovery(identityKey, command, "CLAIMED");
  if (!saveStoredRecovery(recovery)) {
    recoveryUnavailable = true;
    return { status: "RECOVERY_STORAGE_UNAVAILABLE" };
  }
  pendingRecovery = recovery;
  return { status: "OWNED", recovery };
}

function verifyRecoveryForDispatch(
  recovery: ServiceRequestTransitionRecovery,
  identityKey: string,
): boolean {
  hydrateStoredRecovery();
  if (
    recoveryUnavailable ||
    !pendingRecovery ||
    pendingRecovery !== recovery ||
    recovery.ownerIdentityKey !== identityKey ||
    !verifyStoredRecovery(recovery)
  ) {
    recoveryUnavailable = true;
    return false;
  }
  return true;
}

export function beginServiceRequestTransitionDispatch(
  recovery: ServiceRequestTransitionRecovery,
  identityKey: string,
): boolean {
  if (dispatchingRecovery || !verifyRecoveryForDispatch(recovery, identityKey)) return false;
  dispatchingRecovery = recovery;
  return true;
}

export function finishServiceRequestTransitionDispatch(
  recovery: ServiceRequestTransitionRecovery,
  identityKey: string,
): boolean {
  if (dispatchingRecovery !== recovery || recovery.ownerIdentityKey !== identityKey) return false;
  dispatchingRecovery = null;
  return true;
}

export function markServiceRequestTransitionOutcomeUnknown(
  recovery: ServiceRequestTransitionRecovery,
  identityKey: string,
): boolean {
  if (!verifyRecoveryForDispatch(recovery, identityKey)) return false;
  const unknown =
    recovery.state === "OUTCOME_UNKNOWN"
      ? recovery
      : immutableRecovery(identityKey, recovery.command, "OUTCOME_UNKNOWN");
  if (!saveStoredRecovery(unknown)) {
    recoveryUnavailable = true;
    return false;
  }
  pendingRecovery = unknown;
  return true;
}

export function clearServiceRequestTransitionRecovery(
  recovery: ServiceRequestTransitionRecovery,
  identityKey: string,
): boolean {
  if (
    recoveryUnavailable ||
    !pendingRecovery ||
    pendingRecovery !== recovery ||
    recovery.ownerIdentityKey !== identityKey
  ) {
    return false;
  }
  const access = sessionStorageAccess();
  if (access.browser) {
    if (!access.storage) {
      recoveryUnavailable = true;
      return false;
    }
    try {
      const stored = readStoredRecovery(access.storage);
      if (!stored || !recoveryEquals(stored, recovery)) {
        recoveryUnavailable = true;
        return false;
      }
      access.storage.removeItem(SERVICE_REQUEST_TRANSITION_RECOVERY_STORAGE_KEY);
      if (access.storage.getItem(SERVICE_REQUEST_TRANSITION_RECOVERY_STORAGE_KEY) !== null) {
        recoveryUnavailable = true;
        return false;
      }
    } catch {
      recoveryUnavailable = true;
      return false;
    }
  }
  pendingRecovery = null;
  return true;
}

export function resetServiceRequestTransitionRecoveryForTests(): void {
  pendingRecovery = null;
  dispatchingRecovery = null;
  hydrated = false;
  recoveryUnavailable = false;
  const access = sessionStorageAccess();
  try {
    access.storage?.removeItem(SERVICE_REQUEST_TRANSITION_RECOVERY_STORAGE_KEY);
  } catch {
    // Test cleanup also resets the in-memory fail-closed state.
  }
}

export function resetServiceRequestTransitionRecoveryMemoryForTests(): void {
  pendingRecovery = null;
  dispatchingRecovery = null;
  hydrated = false;
  recoveryUnavailable = false;
}
